import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Snackbar from '@mui/material/Snackbar';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useRef, useState } from 'react';

import { DateSelectArg, EventClickArg, EventDropArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { EventResizeDoneArg } from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import timelinePlugin from '@fullcalendar/timeline';

import { FormikValues } from 'formik';
import Loader from 'ui-component/Loader';
import MainCard from 'ui-component/cards/MainCard';
import SubCard from 'ui-component/cards/SubCard';
import AddEventForm from './AddEventForm';
import CalendarStyled from './CalendarStyled';
import Toolbar from './Toolbar';

import { dispatch, RootState, useSelector } from 'store';
import {
  addEvent,
  getEvents,
  removeEvent,
  updateEvent
} from '../../../slices/NdisSlices/calenderEvents/CalenderEventsSlice';

import AddAlarmTwoToneIcon from '@mui/icons-material/AddAlarmTwoTone';
import { Box } from '@mui/system';
import { useAppSelector } from 'app/hooks';
import { useLocation } from 'react-router';
import { DateRange } from 'types';

interface CalenderEventsProps {
  title?: string;
  secondary?: React.ReactNode;
}

const CalenderEvents: React.FC<CalenderEventsProps> = ({ title, secondary }) => {
  const calendarRef = useRef<FullCalendar>(null);
  const matchSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [loading, setLoading] = useState<boolean>(true);
  const [events, setEvents] = useState<FormikValues[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const calendarState = useSelector((state) => state?.calendarEvents);

  // const selectedClient = JSON.parse(localStorage.getItem('selectedClient') || 'null');

  const selectedClient = useSelector((state: RootState) => state.homeTilesServices.selectedClient);

  console.log('selectedClient', selectedClient);

  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);

  const selectedServiceTitle = useAppSelector(
    (state) => state.serviceSelected.selectedServiceTitle
  );

  const location = useLocation();
  const componentParentId = location.pathname;

  const userDetailsString = localStorage.getItem('user-details');
  const userDetails = JSON.parse(userDetailsString || '{}');
  const parsedSelectedClient = JSON.parse(localStorage.getItem('selectedClient') || '{}');
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
    projectId?: string;
    projectName?: string;
    isAdmin?: boolean;
  };

  const isAdminLogin =
    selectedProject?.isAdmin === true &&
    userDetails?.role?.some((r: any) => r.label.toLowerCase() === 'admin');

  const payload = {
    group_id: selectedProjectId,
    service_name_id: selectedServiceTitle || '',
    component_parent_id: componentParentId,
    nested_component_id: 'weekly-patients-schedule',
    user_whoRequested_id: selectedClient?.label || '',
    loggedIN_user_id: userDetails?.name,
    loggedIN_user_email: userDetails?.email,
    client_name: parsedSelectedClient.label || '',
    isAdminLogin
  };

  useEffect(() => {
    dispatch(getEvents(payload)).then(() => setLoading(false));
  }, [selectedProjectId, selectedServiceTitle, selectedClient]);

  useEffect(() => {
    setEvents(calendarState?.events || []);
  }, [calendarState]);

  const [date, setDate] = useState(new Date());
  const [view, setView] = useState(matchSm ? 'listWeek' : 'timeGridWeek');

  const handleDateToday = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.today();
      setDate(calendarApi.getDate());
    }
  };

  const handleViewChange = (newView: string) => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.changeView(newView);
      setView(newView);
    }
  };

  useEffect(() => {
    handleViewChange(matchSm ? 'listWeek' : 'timeGridWeek');
  }, [matchSm]);

  const handleDatePrev = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.prev();
      setDate(calendarApi.getDate());
    }
  };

  const handleDateNext = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.next();
      setDate(calendarApi.getDate());
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState<DateRange | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<FormikValues | null>(null);

  const handleRangeSelect = (arg: DateSelectArg) => {
    if (!isAdminLogin) {
      setSnackbarOpen(true);
      return;
    }
    calendarRef.current?.getApi().unselect();
    setSelectedRange({ start: arg.start, end: arg.end });
    setIsModalOpen(true);
  };

  const handleEventSelect = (arg: EventClickArg) => {
    console.log('events', events);
    const selectEvent =
      events?.length > 0 && events?.find((_event: FormikValues) => _event.id === arg.event.id);
    if (selectEvent) {
      setSelectedEvent({
        ...selectEvent,
        manager: selectEvent.manager_name || selectEvent.manager || '',
        support_worder: selectEvent.support_worker || selectEvent.support_worder || '',
        client_name: selectEvent.client_name || '',
        support_item_number: selectEvent.support_item_number || '',
        managers_email: selectEvent.managers_email || '',
        supportworkers_email: selectEvent.supportworkers_email || '',
        start: selectEvent.start ? new Date(selectEvent.start) : null,
        end: selectEvent.end ? new Date(selectEvent.end) : null
      });
    } else {
      setSelectedEvent(null);
    }
    console.log('selectedEvent', selectEvent);
    setIsModalOpen(true);
  };

  const handleEventUpdate = async ({ event }: EventResizeDoneArg | EventDropArg) => {
    if (!isAdminLogin) {
      setSnackbarOpen(true);
      return;
    }
    console.log('event', event);
    const updatePayload = {
      ...payload,
      event_id: event.id,
      title: event.title,
      description: event.extendedProps.description || '',
      start: event.start,
      end: event.end,
      allDay: event.allDay,
      color: event.backgroundColor || '#1976d2',
      textColor: event.textColor || '#ffffff',
      manager: event.extendedProps.manager_name || event.extendedProps.manager || '',
      support_worder:
        event.extendedProps.support_worker || event.extendedProps.support_worder || '',
      client_name: event.extendedProps.client_name || '',
      support_item_number: event.extendedProps.support_item_number || '',
      managers_email: event.extendedProps.managers_email || '',
      supportworkers_email: event.extendedProps.supportworkers_email || ''
    };
    console.log('event+updatePayload', event, updatePayload);
    dispatch(updateEvent(updatePayload));
  };

  const handleEventCreate = (data: FormikValues) => {
    if (!isAdminLogin) {
      setSnackbarOpen(true);
      return;
    }
    const createPayload = {
      ...payload,
      ...data,
      manager: data.manager || '',
      support_worder: data.support_worder || '',
      client_name: data.client_name || '',
      support_item_number: data.support_item_number || '',
      managers_email: data.managers_email || '',
      supportworkers_email: data.supportworkers_email || ''
    };
    console.log('createPayload', JSON.stringify(createPayload, null, 2));
    dispatch(addEvent(createPayload));
    handleModalClose();
  };

  const handleUpdateEvent = (eventId: string, update: FormikValues) => {
    if (!isAdminLogin) {
      setSnackbarOpen(true);
      return;
    }
    const updatePayload = {
      ...payload,
      event_id: eventId,
      ...update,
      manager: update.manager || '',
      support_worder: update.support_worder || '',
      client_name: update.client_name || '',
      support_item_number: update.support_item_number || '',
      managers_email: update.managers_email || '',
      supportworkers_email: update.supportworkers_email || ''
    };
    console.log('updatePayload', JSON.stringify(updatePayload, null, 2));
    dispatch(updateEvent(updatePayload));
    handleModalClose();
  };

  const handleEventDelete = (
    eventId: string,
    selectedManager: string,
    selectedSupportWorker: string
  ) => {
    if (!isAdminLogin) {
      setSnackbarOpen(true);
      return;
    }
    const deletePayload = {
      ...payload,
      event_id: eventId,
      manager: selectedEvent?.manager || '',
      support_worder: selectedEvent?.support_worder || '',
      client_name: selectedEvent?.client_name || '',
      support_item_number: selectedEvent?.support_item_number || '',
      managers_email: selectedEvent?.managers_email || selectedManager || '',
      supportworkers_email: selectedEvent?.supportworkers_email || selectedSupportWorker || ''
    };
    console.log('deletePayload', JSON.stringify(deletePayload, null, 2));
    dispatch(removeEvent(deletePayload));
    handleModalClose();
  };

  const handleAddClick = () => {
    if (!isAdminLogin) {
      setSnackbarOpen(true);
      return;
    }
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
    setSelectedRange(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  console.log('events', events);

  if (loading) return <Loader />;

  return (
    <MainCard
      title={title || 'Event Calendar'}
      secondary={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {secondary}
          {isAdminLogin && (
            <Button color="secondary" variant="contained" onClick={handleAddClick}>
              <AddAlarmTwoToneIcon fontSize="small" sx={{ mr: 0.75 }} />
              Add New Event
            </Button>
          )}
        </Box>
      }
    >
      <CalendarStyled>
        <Toolbar
          date={date}
          view={view}
          onClickNext={handleDateNext}
          onClickPrev={handleDatePrev}
          onClickToday={handleDateToday}
          onChangeView={handleViewChange}
        />
        <SubCard>
          <FullCalendar
            weekends
            editable={isAdminLogin}
            droppable={isAdminLogin}
            selectable={isAdminLogin}
            events={events}
            ref={calendarRef}
            rerenderDelay={10}
            initialDate={date}
            initialView={view}
            dayMaxEventRows={3}
            eventDisplay="block"
            headerToolbar={false}
            allDayMaintainDuration
            eventResizableFromStart
            select={handleRangeSelect}
            eventDrop={handleEventUpdate}
            eventClick={handleEventSelect}
            eventResize={handleEventUpdate}
            height={matchSm ? 'auto' : 720}
            plugins={[listPlugin, dayGridPlugin, timelinePlugin, timeGridPlugin, interactionPlugin]}
          />
        </SubCard>
      </CalendarStyled>

      <Dialog
        maxWidth="sm"
        fullWidth
        onClose={handleModalClose}
        open={isModalOpen}
        sx={{ '& .MuiDialog-paper': { p: 0 } }}
      >
        {isModalOpen && (
          <AddEventForm
            event={selectedEvent}
            range={selectedRange}
            onCancel={handleModalClose}
            handleDelete={handleEventDelete}
            handleCreate={handleEventCreate}
            handleUpdate={handleUpdateEvent}
          />
        )}
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
          You are not authorized to add or update events
        </Alert>
      </Snackbar>
    </MainCard>
  );
};

export default CalenderEvents;
